import React from 'react';
import { SlideWrapper, GlassCard, SectionLabel } from './SharedComponents';

// ============ SLIDE 6: IMPACT PARADOX ============
export const ImpactParadoxSlide = ({ slide }) => {
  const { left, right } = slide.comparison;
  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0308] via-[#060610] to-[#030810]" />
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[700px] bg-[#f43f5e] opacity-[0.06] blur-[180px] rounded-full" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[500px] h-[700px] bg-[#0068ff] opacity-[0.06] blur-[180px] rounded-full" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1px] h-full bg-gradient-to-b from-transparent via-white/[0.06] to-transparent" />
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
        <SectionLabel delay={0}>The Paradox</SectionLabel>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center max-w-5xl mt-5 mb-12 animate-slide-up leading-relaxed">{slide.headline}</h2>
        <div className="flex items-stretch justify-center gap-6 w-full max-w-5xl">
          <div className="flex-1 animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <GlassCard className="p-7 h-full" style={{ background: `linear-gradient(135deg, ${left.color}12, ${left.color}05)`, borderColor: `${left.color}25` }}>
              <div className="inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-5" style={{ background: left.color, color: '#fff' }}>Viral</div>
              <div className="flex items-center gap-3 mb-5">
                <svg viewBox="0 0 24 24" className="w-7 h-7" style={{ color: left.color }} fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                <span className="text-2xl font-bold" style={{ color: left.color }}>{left.label}</span>
              </div>
              <div className="space-y-2.5">
                {left.items.map((item, idx) => (
                  <div key={idx} className="h-10 rounded-lg flex items-center px-4" style={{
                    width: `${100 - idx * 8}%`, background: `linear-gradient(90deg, ${left.color}30, ${left.color}10)`, border: `1px solid ${left.color}20`
                  }}>
                    <span className="text-white text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-4 text-white/30 text-xs"><span>Spreads in hours</span></div>
            </GlassCard>
          </div>
          <div className="flex flex-col items-center justify-center animate-scale-in" style={{ animationDelay: '0.3s' }}>
            <div className="w-[1px] h-16 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
            <div className="w-12 h-12 rounded-full bg-white/[0.04] border border-white/[0.12] flex items-center justify-center my-3">
              <span className="text-white/50 font-bold text-xs">VS</span>
            </div>
            <div className="w-[1px] h-16 bg-gradient-to-b from-white/20 via-transparent to-transparent" />
          </div>
          <div className="flex-1 animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <GlassCard className="p-7 h-full" style={{ background: `linear-gradient(135deg, ${right.color}12, ${right.color}05)`, borderColor: `${right.color}25` }}>
              <div className="inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-5" style={{ background: right.color, color: '#fff' }}>Impact</div>
              <div className="flex items-center gap-3 mb-5">
                <svg viewBox="0 0 24 24" className="w-7 h-7" style={{ color: right.color }} fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>
                <span className="text-2xl font-bold" style={{ color: right.color }}>{right.label}</span>
              </div>
              <div className="space-y-2.5">
                {right.items.map((item, idx) => (
                  <div key={idx} className="h-10 rounded-lg flex items-center justify-end px-4" style={{
                    width: `${75 + idx * 5}%`, marginLeft: 'auto', background: `linear-gradient(90deg, ${right.color}10, ${right.color}30)`, border: `1px solid ${right.color}20`
                  }}>
                    <span className="text-white text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-end gap-2 mt-4 text-white/30 text-xs"><span>Takes years to build</span></div>
            </GlassCard>
          </div>
        </div>
      </div>
    </SlideWrapper>
  );
};

// ============ SLIDE 7: BIG QUESTION ============
export const BigQuestionSlide = ({ slide }) => (
  <SlideWrapper slide={slide} background={
    <div className="fixed inset-0 z-0">
      <div className="absolute inset-0 bg-[#020206]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1600px] h-[1600px] bg-[#0068ff] opacity-[0.12] blur-[300px] rounded-full" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#0068ff] opacity-[0.06] blur-[150px] rounded-full" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[500px] font-black text-[#0068ff] opacity-[0.03] select-none pointer-events-none leading-none">?</div>
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]" viewBox="0 0 1920 1080">
        <circle cx="960" cy="540" r="250" fill="none" stroke="#0068ff" strokeWidth="1" />
        <circle cx="960" cy="540" r="350" fill="none" stroke="#0068ff" strokeWidth="0.5" className="animate-pulse" />
        <circle cx="960" cy="540" r="450" fill="none" stroke="#0068ff" strokeWidth="0.3" strokeDasharray="8 8" />
      </svg>
      <div className="absolute top-12 left-12 w-20 h-20"><div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#0068ff]/40 to-transparent" /><div className="absolute top-0 left-0 h-full w-[2px] bg-gradient-to-b from-[#0068ff]/40 to-transparent" /></div>
      <div className="absolute top-12 right-12 w-20 h-20"><div className="absolute top-0 right-0 w-full h-[2px] bg-gradient-to-l from-[#0068ff]/40 to-transparent" /><div className="absolute top-0 right-0 h-full w-[2px] bg-gradient-to-b from-[#0068ff]/40 to-transparent" /></div>
      <div className="absolute bottom-12 left-12 w-20 h-20"><div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#0068ff]/40 to-transparent" /><div className="absolute bottom-0 left-0 h-full w-[2px] bg-gradient-to-t from-[#0068ff]/40 to-transparent" /></div>
      <div className="absolute bottom-12 right-12 w-20 h-20"><div className="absolute bottom-0 right-0 w-full h-[2px] bg-gradient-to-l from-[#0068ff]/40 to-transparent" /><div className="absolute bottom-0 right-0 h-full w-[2px] bg-gradient-to-t from-[#0068ff]/40 to-transparent" /></div>
    </div>
  }>
    <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
      <div className="relative text-center animate-scale-in">
        <h2 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black leading-[1.1] tracking-tight">
          <span className="text-white">What if we could</span>
        </h2>
        <h2 className="text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-black leading-[1.1] tracking-tight mt-2">
          <span className="bg-gradient-to-r from-[#0068ff] via-[#3d8bff] to-[#0068ff] bg-clip-text text-transparent">measure</span>
        </h2>
        <h2 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black leading-[1.1] tracking-tight mt-2">
          <span className="text-white">human </span>
          <span className="bg-gradient-to-r from-[#3d8bff] to-[#0068ff] bg-clip-text text-transparent">contribution</span>
          <span className="text-[#0068ff]">?</span>
        </h2>
      </div>
      <div className="mt-10 w-32 h-[2px] bg-gradient-to-r from-transparent via-[#0068ff]/60 to-transparent animate-fade-in" style={{ animationDelay: '0.4s' }} />
      <p className="mt-6 text-lg md:text-xl text-white/35 tracking-wide font-light animate-fade-in" style={{ animationDelay: '0.6s' }}>{slide.subtext}</p>
    </div>
  </SlideWrapper>
);

// ============ SLIDE 8: BREAKTHROUGH ============
const TechIcon = ({ type, color }) => {
  const icons = {
    blockchain: <svg viewBox="0 0 24 24" className="w-10 h-10" fill="none" stroke={color} strokeWidth="1.5"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><path d="M10 6.5h4M6.5 10v4M17.5 10v4M10 17.5h4" /></svg>,
    sensors: <svg viewBox="0 0 24 24" className="w-10 h-10" fill="none" stroke={color} strokeWidth="1.5"><circle cx="12" cy="12" r="3" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4" /><circle cx="12" cy="12" r="8" strokeDasharray="4 4" /></svg>,
    ai: <svg viewBox="0 0 24 24" className="w-10 h-10" fill="none" stroke={color} strokeWidth="1.5"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 4-6 8-6s8 2 8 6" /><circle cx="12" cy="8" r="1" fill={color} /></svg>
  };
  return icons[type] || null;
};

export const BreakthroughSlide = ({ slide }) => {
  const techColors = ['#8b5cf6', '#06b6d4', '#0068ff'];
  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#0a0a14] to-[#030308]" />
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-[#8b5cf6] opacity-[0.06] blur-[150px] rounded-full" />
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-[#06b6d4] opacity-[0.06] blur-[150px] rounded-full" />
        <div className="absolute bottom-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-[#0068ff] opacity-[0.08] blur-[180px] rounded-full" />
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" viewBox="0 0 1920 1080">
          <line x1="480" y1="270" x2="960" y2="750" stroke="#8b5cf6" strokeWidth="1" strokeDasharray="4 8" />
          <line x1="1440" y1="270" x2="960" y2="750" stroke="#06b6d4" strokeWidth="1" strokeDasharray="4 8" />
          <line x1="480" y1="270" x2="1440" y2="270" stroke="#fff" strokeWidth="0.5" strokeDasharray="4 8" />
        </svg>
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
        <SectionLabel delay={0}>The Convergence</SectionLabel>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-5 mb-3 animate-slide-up">{slide.title}</h2>
        <p className="text-white/40 text-lg mb-14 animate-fade-in" style={{ animationDelay: '0.1s' }}>{slide.subtitle}</p>
        <div className="relative w-[850px] h-[380px]">
          {slide.technologies.map((tech, idx) => {
            const positions = [
              { top: 0, left: 0, width: '260px' },
              { top: 0, right: 0, width: '260px' },
              { bottom: 0, left: '50%', marginLeft: '-150px', width: '300px' }
            ];
            const isMain = idx === 2;
            return (
              <div key={idx} className="absolute animate-slide-up" style={{ ...positions[idx], animationDelay: `${0.2 + idx * 0.1}s` }}>
                <GlassCard className="p-6 hover:scale-[1.03] transition-transform duration-300" style={{
                  background: `linear-gradient(135deg, ${techColors[idx]}12, ${techColors[idx]}05)`,
                  borderColor: `${techColors[idx]}${isMain ? '50' : '25'}`,
                  boxShadow: isMain ? `0 0 50px ${techColors[idx]}20` : 'none'
                }}>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${techColors[idx]}18` }}>
                      <TechIcon type={tech.icon} color={techColors[idx]} />
                    </div>
                    <h3 className="text-xl font-bold" style={{ color: techColors[idx] }}>{tech.name}</h3>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed">{tech.description}</p>
                  {isMain && <div className="absolute -top-1.5 -right-1.5 w-3 h-3"><span className="absolute inline-flex h-full w-full rounded-full bg-[#0068ff] opacity-75 animate-ping" /><span className="relative inline-flex rounded-full h-3 w-3 bg-[#0068ff]" /></div>}
                </GlassCard>
              </div>
            );
          })}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 850 380">
            <defs><marker id="acv" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto"><polygon points="0 0,7 2.5,0 5" fill="#0068ff" opacity="0.7"/></marker></defs>
            <path d="M 180 120 Q 320 210 395 250" fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeOpacity="0.4" strokeDasharray="5 4" markerEnd="url(#acv)" />
            <path d="M 670 120 Q 530 210 455 250" fill="none" stroke="#06b6d4" strokeWidth="1.5" strokeOpacity="0.4" strokeDasharray="5 4" markerEnd="url(#acv)" />
          </svg>
          <div className="absolute" style={{ top: '46%', left: '50%', marginLeft: '-20px', marginTop: '-20px' }}>
            <div className="w-10 h-10 rounded-full bg-[#0068ff]/15 border border-[#0068ff]/30 flex items-center justify-center animate-pulse">
              <div className="w-4 h-4 rounded-full bg-[#0068ff]/30" />
            </div>
          </div>
        </div>
        <GlassCard delay={0.6} className="mt-8 px-6 py-2.5" style={{ borderColor: '#0068ff30' }}>
          <span className="text-[#0068ff] font-medium text-sm">= Verifiable Contribution</span>
        </GlassCard>
      </div>
    </SlideWrapper>
  );
};

// ============ SLIDE 9: PROOF OF IMPACT ============
const FlowIcon = ({ type, color }) => {
  const icons = {
    book: <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke={color} strokeWidth="1.5"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>,
    leaf: <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke={color} strokeWidth="1.5"><path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.66.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75" /></svg>,
    heart: <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke={color} strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" /></svg>,
    lightbulb: <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke={color} strokeWidth="1.5"><path d="M9 21h6m-3-3v3m-4-7.5c0-2.485 2-4.5 4-4.5s4 2.015 4 4.5c0 1.5-1 2.5-2 3.5H11c-1-1-2-2-2-3.5z" /><path d="M12 3v1m-7.07 2.93l.7.7M3 12h1m14 0h1m-2.63-5.37l.7-.7" /></svg>
  };
  return icons[type] || null;
};

export const ProofOfImpactSlide = ({ slide }) => (
  <SlideWrapper slide={slide} background={
    <div className="fixed inset-0 z-0">
      <div className="absolute inset-0 bg-gradient-to-br from-[#030308] via-[#040818] to-[#030308]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[800px] bg-[#0068ff] opacity-[0.08] blur-[200px] rounded-full" />
      <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-[#8b5cf6] opacity-[0.04] blur-[120px] rounded-full" />
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#22c55e] opacity-[0.03] blur-[120px] rounded-full" />
      <svg className="absolute inset-0 w-full h-full opacity-[0.02]" viewBox="0 0 1920 1080">
        <line x1="200" y1="540" x2="1720" y2="540" stroke="#0068ff" strokeWidth="1" strokeDasharray="8 8" />
      </svg>
    </div>
  }>
    <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
      <SectionLabel delay={0}>The New Standard</SectionLabel>
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-5 mb-3 animate-slide-up">{slide.title}</h2>
      <p className="text-white/40 text-lg mb-10 animate-fade-in" style={{ animationDelay: '0.1s' }}>{slide.subtitle}</p>
      <div className="flex items-center gap-4 mb-10 animate-slide-up" style={{ animationDelay: '0.2s' }}>
        <GlassCard className="px-8 py-5" style={{ borderColor: '#0068ff40', boxShadow: '0 0 30px rgba(0,104,255,0.15)' }}>
          <span className="text-2xl font-bold text-white">{slide.flow.from}</span>
        </GlassCard>
        <div className="flex items-center gap-2">
          <div className="w-16 h-[2px] bg-gradient-to-r from-[#0068ff]/60 to-[#22c55e]/60" />
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#22c55e]" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
          <div className="w-16 h-[2px] bg-gradient-to-r from-[#22c55e]/60 to-[#0068ff]/60" />
        </div>
        <GlassCard className="px-8 py-5" style={{ borderColor: '#22c55e40', boxShadow: '0 0 30px rgba(34,197,94,0.15)' }}>
          <span className="text-2xl font-bold bg-gradient-to-r from-[#22c55e] to-[#0068ff] bg-clip-text text-transparent">{slide.flow.to}</span>
        </GlassCard>
      </div>
      <div className="grid grid-cols-4 gap-5 w-full max-w-4xl">
        {slide.examples.map((ex, idx) => (
          <GlassCard key={idx} delay={0.3 + idx * 0.1} className="p-5 text-center group hover:scale-[1.04] transition-transform duration-300" style={{
            background: `linear-gradient(135deg, ${ex.color}10, ${ex.color}05)`, borderColor: `${ex.color}20`
          }}>
            <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: `${ex.color}15` }}>
              <FlowIcon type={ex.icon} color={ex.color} />
            </div>
            <span className="text-white font-semibold text-sm">{ex.label}</span>
          </GlassCard>
        ))}
      </div>
    </div>
  </SlideWrapper>
);

// ============ SLIDE 10: IMPACT EQUATION ============
export const EquationSlide = ({ slide }) => {
  const parts = slide.equation.parts;
  const operators = slide.equation.operators;
  return (
    <SlideWrapper slide={slide} background={
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030308] via-[#060614] to-[#030308]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] bg-[#0068ff] opacity-[0.07] blur-[180px] rounded-full" />
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: 'linear-gradient(rgba(0,104,255,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(0,104,255,0.2) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" viewBox="0 0 1920 1080">
          <text x="100" y="200" fill="#0068ff" fontSize="120" fontWeight="800" opacity="0.3">=</text>
          <text x="1600" y="900" fill="#0068ff" fontSize="120" fontWeight="800" opacity="0.3">f(x)</text>
        </svg>
      </div>
    }>
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
        <SectionLabel delay={0}>The Formula</SectionLabel>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mt-5 mb-14 animate-slide-up">{slide.title}</h2>
        <GlassCard delay={0.2} className="px-10 py-8" style={{ borderColor: '#0068ff30', boxShadow: '0 0 60px rgba(0,104,255,0.1)' }}>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            {parts.map((part, idx) => (
              <span key={idx} className={`text-2xl md:text-3xl lg:text-4xl font-bold ${operators[idx] ? 'text-white/30 mx-1' : 'text-white'}`}
                style={!operators[idx] && idx === 0 ? { color: '#0068ff' } : !operators[idx] ? { color: '#fff' } : {}}>
                {part}
              </span>
            ))}
          </div>
        </GlassCard>
        <div className="grid grid-cols-4 gap-4 mt-10 max-w-4xl w-full">
          {['Action', 'Quality', 'Verification', 'Network'].map((item, idx) => {
            const colors = ['#22c55e', '#8b5cf6', '#0068ff', '#06b6d4'];
            const descs = ['What you do', 'How well', 'Proof it happened', 'Ripple effect'];
            return (
              <GlassCard key={idx} delay={0.4 + idx * 0.1} className="p-5 text-center" style={{ borderColor: `${colors[idx]}20` }}>
                <div className="w-3 h-3 rounded-full mx-auto mb-3" style={{ background: colors[idx], boxShadow: `0 0 12px ${colors[idx]}60` }} />
                <div className="text-white font-semibold mb-1">{item}</div>
                <div className="text-white/35 text-xs">{descs[idx]}</div>
              </GlassCard>
            );
          })}
        </div>
      </div>
    </SlideWrapper>
  );
};
